import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ProductCard } from './ProductCard';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function ProductRow({ title, products, className }) {
  const { i18n } = useTranslation();
  const scrollRef = useRef(null);
  const isRTL = i18n.dir() === 'rtl';

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      const currentScroll = scrollRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollRef.current.scrollTo({
        left: isRTL ? -newScroll : newScroll,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) return null;

  return (
    <section className={cn("relative", className)} data-testid="product-row">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-4 md:px-0">
        <h2 className="text-xl md:text-2xl font-bold text-white">
          {title}
        </h2>
        
        {/* Navigation arrows - Desktop */}
        <div className="hidden md:flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="border-gray-700 text-white hover:bg-gray-800 hover:text-mutagim-red"
            onClick={() => scroll('left')}
            data-testid="scroll-left"
          >
            {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="border-gray-700 text-white hover:bg-gray-800 hover:text-mutagim-red"
            onClick={() => scroll('right')}
            data-testid="scroll-right"
          >
            {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Scrollable container */}
      <div 
        ref={scrollRef}
        className={cn(
          "flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth",
          "px-4 md:px-0 -mx-4 md:mx-0 pb-4",
          // RTL scroll support
          isRTL && "flex-row-reverse"
        )}
        style={{ 
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {products.map((product) => (
          <div 
            key={product.id || product.slug}
            className="flex-shrink-0 w-[200px] md:w-[240px]"
            style={{ scrollSnapAlign: 'start' }}
          >
            <ProductCard product={product} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default ProductRow;
